"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const L = {
    fr: {
        title: "Calculez le retour sur investissement",
        frequency: "Fois par semaine",
        duration: "Durée à la main (minutes)",
        build: "Temps de construction (heures)",
        watch: "Surveillance par an (heures)",
        tools: "Outils par mois (€)",
        saved: "Économisé par an",
        first: "Gain net la première année",
        next: "Puis chaque année",
        payback: "Rentabilisé en",
        never: "Jamais rentabilisé",
        months: "mois",
        hours: "h",
        verdictGood: "Le calcul est favorable : cette tâche mérite d'être automatisée.",
        verdictTight: "C'est serré. Automatisez si la fiabilité compte autant que le temps.",
        verdictBad: "Ne l'automatisez pas. Le temps investi ne sera pas récupéré.",
        note: "Le coût des outils est converti en heures à 50 € de l'heure, pour tout ramener à une seule unité. Le temps de construction, lui, est presque toujours sous-estimé : doublez votre première intuition.",
    },
    en: {
        title: "Work out the return on investment",
        frequency: "Times per week",
        duration: "Manual duration (minutes)",
        build: "Build time (hours)",
        watch: "Supervision per year (hours)",
        tools: "Tools per month (€)",
        saved: "Saved per year",
        first: "Net gain in year one",
        next: "Then every year",
        payback: "Pays back in",
        never: "Never pays back",
        months: "months",
        hours: "h",
        verdictGood: "The maths works: this task is worth automating.",
        verdictTight: "It is tight. Automate if reliability matters as much as time.",
        verdictBad: "Do not automate it. The time invested will not come back.",
        note: "Tool costs are converted into hours at €50 an hour, so everything comes back to one unit. Build time is almost always underestimated: double your first instinct.",
    },
};

// Valorisation d'une heure, uniquement pour comparer le coût des outils au
// temps gagné. Volontairement fixe : la démo compare des heures, pas des devis.
const HOURLY = 50;

export default function RoiCalculator({ lang = "fr" }) {
    const t = L[lang] || L.fr;

    const [frequency, setFrequency] = useState(5);
    const [duration, setDuration] = useState(3);
    const [build, setBuild] = useState(4);
    const [watch, setWatch] = useState(2);
    const [tools, setTools] = useState(25);

    const r = useMemo(() => {
        const savedHours = (frequency * 52 * duration) / 60;
        const toolsHours = (tools * 12) / HOURLY;

        const firstYear = savedHours - build - watch - toolsHours;
        const nextYears = savedHours - watch - toolsHours;

        // Mois nécessaires pour effacer le temps de construction.
        const monthlyNet = nextYears / 12;
        const payback = monthlyNet > 0 ? build / monthlyNet : null;

        return { savedHours, firstYear, nextYears, payback };
    }, [frequency, duration, build, watch, tools]);

    const verdict =
        r.nextYears <= 0
            ? { text: t.verdictBad, tone: "text-red-600 dark:text-red-400" }
            : r.firstYear <= 0
              ? { text: t.verdictTight, tone: "text-amber-600 dark:text-amber-400" }
              : { text: t.verdictGood, tone: "text-green-700 dark:text-green-400" };

    const h = (value) =>
        `${Math.round(value).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} ${t.hours}`;

    const field = (label, value, setValue, min, max, step, suffix = "") => (
        <div>
            <div className="flex justify-between items-baseline mb-1.5">
                <label className="text-sm text-gray-600 dark:text-lightBlue mb-0">
                    {label}
                </label>
                <span className="text-sm font-bold tabular-nums dark:text-white">
                    {value}
                    {suffix}
                </span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => setValue(Number(e.target.value))}
                className="w-full accent-primary cursor-pointer"
            />
        </div>
    );

    return (
        <div className="my-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5 sm:p-6 text-base leading-normal">
            <div className="flex items-center gap-2 mb-5">
                <SolarIcon
                    name="chart-square-linear"
                    className="w-5 h-5 text-primary"
                />
                <div className="font-bold dark:text-white text-base leading-snug">
                    {t.title}
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-4">
                    {field(t.frequency, frequency, setFrequency, 1, 50, 1)}
                    {field(t.duration, duration, setDuration, 1, 120, 1)}
                    {field(t.build, build, setBuild, 1, 60, 1)}
                    {field(t.watch, watch, setWatch, 0, 40, 1)}
                    {field(t.tools, tools, setTools, 0, 300, 5)}
                </div>

                <div className="flex flex-col justify-center rounded-xl bg-primary/[0.06] dark:bg-primary/10 p-5">
                    <div className="text-sm text-gray-600 dark:text-lightBlue mb-0">
                        {t.saved}
                    </div>
                    <div className="text-4xl font-bold text-primary tabular-nums my-1">
                        {h(r.savedHours)}
                    </div>

                    <div className="mt-3 space-y-1 text-sm">
                        <div className="flex justify-between gap-3">
                            <span className="text-gray-600 dark:text-lightBlue">
                                {t.first}
                            </span>
                            <span className="font-bold tabular-nums dark:text-white">
                                {h(r.firstYear)}
                            </span>
                        </div>
                        <div className="flex justify-between gap-3">
                            <span className="text-gray-600 dark:text-lightBlue">
                                {t.next}
                            </span>
                            <span className="font-bold tabular-nums dark:text-white">
                                {h(r.nextYears)}
                            </span>
                        </div>
                        <div className="flex justify-between gap-3">
                            <span className="text-gray-600 dark:text-lightBlue">
                                {t.payback}
                            </span>
                            <span className="font-bold tabular-nums dark:text-white">
                                {r.payback === null || r.payback > 120
                                    ? t.never
                                    : `${Math.max(1, Math.round(r.payback))} ${t.months}`}
                            </span>
                        </div>
                    </div>

                    <div className={`mt-4 text-sm font-semibold mb-0 ${verdict.tone}`}>
                        {verdict.text}
                    </div>
                </div>
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-4 mb-0">
                {t.note}
            </p>
        </div>
    );
}
